import {
    isCampaignDropShipping,
    isCampaignManufacturer,
    isCampaignMarketSearch,
    isCampaignSeller,
    isEfsElektronikUser,
    isManufacturer,
    user
} from "./Utils";

// EFS Elektronik customer-specific tiered-discount columns: each value is the
// user's own price (the product-list `price` field) multiplied by `rate`. Single
// source for the field keys, header labels and rates shared by the product-list
// table, the column picker and the Excel export. Gated to a single user via
// isEfsElektronikUser().
export const EFS_TIERED_PRICE_COLUMNS = [
    {value: "b1_price", label: "B1 (85%)", rate: 0.85},
    {value: "b2_price", label: "B2 (80%)", rate: 0.80},
    {value: "c1_price", label: "C1 (75%)", rate: 0.75},
    {value: "c2_price", label: "C2 (70%)", rate: 0.70},
    {value: "garanti_disi_price", label: "Garanti Dışı (60%)", rate: 0.60},
];

export const exportableFields = (t, campaign) => {

    let fields = [];
    fields.push({label:t('product.id'), value:"id", default: true});
    fields.push({label:t('product.product_name'), value:"name", default: true});
    fields.push({label:t('product.search_key'), value:"search_key", default: false});
    fields.push({label:t('product.properties.'), value:"properties", default: false});
    fields.push({label:t('product.category'), value:"category", default: true});
    fields.push({label:t('product.brand'), value:"brand", default: true});
    fields.push({label:t('product.tags'), value:"tags", default: false});
    fields.push({label:t('product.sku'), value:"sku", default: true});
    fields.push({label:t('product.mpn'), value:"mpn", default: false});
    fields.push({label:t('product.ean'), value:"ean", default: false});
    fields.push({label:t('product.upc'), value:"upc", default: false});
    fields.push({label:t('product.asin'), value:"asin", default: false});
    fields.push({label:t('product.barcode'), value:"barcode", default: false});
    fields.push({label:t('product.status'), value:"is_active", default: false});
    fields.push({label:t('product.image'), value:"image", default: false});
    fields.push({label:t('product.ref_id'), value:"ref_id", default: false});
    fields.push({label:t('price_violation.map_abbreviation'), value:"violation_strategy.min_value", default: false});
    fields.push({label:t('price_violation.mrp_abbreviation'), value:"violation_strategy.max_value", default: false});

    if (!isCampaignMarketSearch(campaign)) {
        fields.push({label: t('product.price'), value: "price", default: true});
        fields.push({label: t('product.currency'), value: "currency", default: true});
        fields.push({label: (isCampaignSeller(campaign) || isCampaignDropShipping(campaign)) ? t('product.my_stock') : t('product.stock'), value: "stock", default: false});
        if (isEfsElektronikUser())
            EFS_TIERED_PRICE_COLUMNS.forEach(col => fields.push({label: col.label, value: col.value, default: false}));
    }

    if (isCampaignDropShipping(campaign)) {
        fields.push({label:t('product.drop_shipping_price'), value:"drop_shipping_price", default: false});
        fields.push({label:t('product.drop_shipping_cost'), value:"drop_shipping_cost", default: false});
        fields.push({label:t('product.drop_shipping_review'), value:"drop_shipping.rating_count", default: false});
        fields.push({label:t('product.drop_shipping_rate'), value:"drop_shipping.rating", default: false});
        fields.push({label:t('product.drop_shipping_best_sellers_rank'), value:"drop_shipping.best_sellers_rank", default: false});
    }

    if (isCampaignSeller(campaign) || isCampaignDropShipping(campaign)) {
        fields.push({label: t('product.product_url'), value: "product_url", default: false});
        fields.push({label: t('product.position'), value: "position", default: false});
        fields.push({label: t('reprice.min_value'), value: "pricing_strategy_min_value", default: false});
        fields.push({label: t('reprice.max_value'), value: "pricing_strategy_max_value", default: false});
        fields.push({label: t('product.target_price'), value: "target_price", default: true});
        fields.push({label: t('product.suggested_price'), value: "suggested_price", default: false});
    }

    // The supplier margin round trip (D11): export the product's own override
    // (blank when it follows the campaign) plus the effective margin and where
    // it comes from, edit, and re-upload through product_upload/supplier_margins.
    if (campaign?.settings?.supplier_module?.is_active) {
        fields.push({label:t('product.supplier_margin'), value:"supplier_margin_rate", default: false});
        fields.push({label:t('product.supplier_effective_margin'), value:"supplier_effective_margin_rate", default: false});
    }

    fields.push({label:t('product.comp_count'), value:"competitorsCount", default: false});
    fields.push({label:t('product.url_count'), value:"url_count", default: false});
    fields.push({label:t('product.comp_min'), value:"competitorsMinPrice", default: true});
    fields.push({label:t('product.comp_min_shipping_price'), value:"competitorsMinShippingPrice", default: false});
    fields.push({label:t('product.comp_avg'), value:"competitorsAvgPrice", default: false});
    if (isCampaignSeller(campaign) || isCampaignDropShipping(campaign)) {
        fields.push({label:t('product.comp_avg_diff'), value:"competitorsAvgPriceDiff", default: false});
    }
    fields.push({label:t('product.comp_max'), value:"competitorsMaxPrice", default: false});
    fields.push({label:t('product.comp_max_shipping_price'), value:"competitorsMaxShippingPrice", default: false});
    fields.push({label:t('product.suggest_count'), value:"competitorSuggestionsCount", default: false});
    fields.push({label:t('product.comp_min_name'), value:"competitorsMinName", default: true});
    fields.push({label:t('product.comp_max_name'), value:"competitorsMaxName", default: false});
    fields.push({label:t('product.shelve_position'), value:"shelve_position", default: false});

    fields.push({label:t('product.review_number'), value:"review_number", default: false});

    campaign?.settings?.special_fields?.forEach(sf=> {
        if (sf.selected && !sf.name.startsWith('competitors.'))
            fields.push({label:t("product."+sf.name), value:sf.name, default: false});
    });

    return fields;
};

export const productListColumns = (t, campaign) => {

    let fields = [];

    fields.push({label:t('product.image'), value:"image", default: true, group:'identity'});
    fields.push({label:t('product.website'), value:"website", default: false, group:'identity'});
    fields.push({label:t('product.sku'), value:"sku", default: false, group:'identity'});
    fields.push({label:t('product.mpn'), value:"mpn", default: false, group:'identity'});
    fields.push({label:t('product.ean'), value:"ean", default: false, group:'identity'});
    fields.push({label:t('product.upc'), value:"upc", default: false, group:'identity'});
    fields.push({label:t('product.asin'), value:"asin", default: false, group:'identity'});
    fields.push({label:t('product.barcode'), value:"barcode", default: false, group:'identity'});
    fields.push({label:t('product.product_name'), value:"name", default: true, group:'identity'});

    fields.push({label:t('product.properties.'), value:"properties", default: false, group:'variant'});
    fields.push({label:t('product.search_key'), value:"search_key", default: false, group:'identity'});
    fields.push({label:t('product.category'), value:"category", default: false, group:'classification'});
    fields.push({label:t('product.categories'), value:"categories", default: false, group:'classification'});
    fields.push({label:t('product.brand'), value:"brand", default: false, group:'classification'});
    fields.push({label:t('product.tags'), value:"tags", default: false, group:'classification'});

    fields.push({label:t('product.rate'), value:"rate", default: false, group:'reviews'});
    fields.push({label:t('product.ref_id'), value:"ref_id", default: false, group:'identity'});
    fields.push({label:t('product.vat'), value:"vat", default: false, group:'price'});
    fields.push({label:t('price_violation.mrp_abbreviation'), value:"mrp", default: true, group:'compliance'});

    fields.push({label: isCampaignManufacturer(campaign) ? t('price_violation.map_abbreviation') : t('product.price'), value:"price", default: true, group:'price'});

    if (isEfsElektronikUser())
        EFS_TIERED_PRICE_COLUMNS.forEach(col => fields.push({label: col.label, value: col.value, default: false, group:'price'}));

    if (isCampaignSeller(campaign) || isCampaignDropShipping(campaign)) {
        fields.push({label: t('product.my_stock'), value: "my_stock", default: false, group:'price'});
        fields.push({label: t('product.position'), value: "position", default: false, group:'price'});
        fields.push({label: t('reprice.min_value'), value: "pricing_strategy_min_value", default: false, group:'price'});
        fields.push({label: t('reprice.max_value'), value: "pricing_strategy_max_value", default: false, group:'price'});
        fields.push({label: t('product.target_price'), value: "target_price", default: false, group:'price'});
        fields.push({label: t('product.suggested_price'), value: "suggested_price", default: false, group:'price'});
        fields.push({label: t('product.discount_type'), value: "discount_type", default: false, group:'price'});
    }

    if (isCampaignDropShipping(campaign)) {
        fields.push({label: t('product.drop_shipping_price'), value: "drop_shipping_price", default: false, group:'dropshipping'});
        fields.push({label: t('product.drop_shipping_cost'), value: "drop_shipping_cost", default: false, group:'dropshipping'});
        fields.push({label: t('product.drop_shipping_review'), value: "drop_shipping_review", default: false, group:'dropshipping'});
        fields.push({label: t('product.drop_shipping_rate'), value: "drop_shipping_rate", default: false, group:'dropshipping'});
        fields.push({label: t('product.drop_shipping_best_sellers_rank'), value: "drop_shipping_best_sellers_rank", default: false, group:'dropshipping'});
    }

    fields.push({label:t('product.comp_range'), value:"competitorsRange", default: true, group:'competition', always: true});
    fields.push({label:t('product.comp_min'), value:"competitorsMinPrice", default: false, group:'competition'});
    fields.push({label:t('product.comp_min_shipping_price'), value:"competitorsMinShippingPrice", default: false, group:'competition'});
    fields.push({label:t('product.comp_avg'), value:"competitorsAvgPrice", default: false, group:'competition'});
    if (isCampaignSeller(campaign) || isCampaignDropShipping(campaign)) {
        fields.push({label:t('product.comp_avg_diff'), value:"competitorsAvgPriceDiff", default: false, group:'competition'});
    }
    fields.push({label:t('product.comp_max'), value:"competitorsMaxPrice", default: false, group:'competition'});
    fields.push({label:t('product.comp_max_shipping_price'), value:"competitorsMaxShippingPrice", default: false, group:'competition'});
    fields.push({label:t('product.url_count'), value:"url_count", default: false, group:'competition'});
    fields.push({label:t('product.comp_count'), value:"competitorsCount", default: true, group:'competition', always: true});
    fields.push({label:t('product.suggest_count'), value:"competitorSuggestionsCount", default: true, group:'competition', always: true});

    if (campaign?.settings?.supplier_module?.is_active) {
        fields.push({label:t('product.supplier_count'), value:"suppliersCount", default: true, group:'supplier', always: true});
        fields.push({label:t('product.supplier_suggest_count'), value:"supplierSuggestionsCount", default: true, group:'supplier', always: true});
    }

    fields.push({label:t('product.comp_min_name'), value:"competitorsMinName", default: false, group:'competition'});
    fields.push({label:t('product.comp_max_name'), value:"competitorsMaxName", default: false, group:'competition'});
    fields.push({label:t('product.status'), value:"is_active", default: false, group:'identity'});
    fields.push({label:t('product.shelve_position'), value:"shelve_position", default: false, group:'price'});

    fields.push({label:t('product.review_number'), value:"review_number", default: false, group:'reviews'});

    campaign?.settings?.special_fields?.forEach(sf=> {
        if (sf.selected && !sf.name.startsWith('competitors.'))
            fields.push({label:t("product."+sf.name), value:sf.name, default: false, group:'variant'});
    });

    return fields;
};

export const getSelectedExportableFields = (t, campaign, refresh) => {
    const allList = exportableFields(t, campaign);
    if (localStorage.getItem("exportFields") && !refresh) {
        try {
            let fields = JSON.parse(localStorage.getItem("exportFields"));
            return fields.filter(i => allList.findIndex(a=> a.value === i) !== -1)
        } catch (e) {
            return  allList.filter(e => e.default).map(e2 => {return e2.value});
        }
    }
    else
        return allList.filter(e => e.default).map(e2 => {return e2.value});
};

export const getSelectedProductListColumns = (t, campaign, refresh, allowedGroups) => {
    const allList = productListColumns(t, campaign);
    const defaults = () => allList
        .filter(e => e.default && (e.always || !allowedGroups || allowedGroups.includes(e.group)))
        .map(e => e.value);
    if (localStorage.getItem("selectedColumns") && !refresh) {
        try {
            let fields = JSON.parse(localStorage.getItem("selectedColumns"))
                .filter(i => allList.findIndex(a=> a.value === i) !== -1);
            // One-time: surface newly added default columns (combined seller price
            // range, seller count) for users who already have a saved column selection.
            if (localStorage.getItem("selectedColumnsMigratedV4") !== "1") {
                ["competitorsCount", "competitorsRange", "mrp", "competitorSuggestionsCount"].forEach(v => {
                    if (!fields.includes(v)) fields.unshift(v);
                });
                localStorage.setItem("selectedColumnsMigratedV4", "1");
                localStorage.setItem("selectedColumns", JSON.stringify(fields));
            }
            // One-time: surface the new supplier columns, but only when the supplier
            // module is actually on for this campaign (allList only has them then).
            // Only stamp it when it actually applied: a user whose first visit
            // after the deploy lands on a campaign with the module off would
            // otherwise burn the migration and never see the columns on the
            // campaign that does have it on.
            if (localStorage.getItem("selectedColumnsMigratedV5") !== "1") {
                const supplierColumns = ["suppliersCount", "supplierSuggestionsCount"]
                    .filter(v => allList.some(a => a.value === v));
                if (supplierColumns.length > 0) {
                    supplierColumns.forEach(v => {
                        if (!fields.includes(v)) fields.unshift(v);
                    });
                    localStorage.setItem("selectedColumnsMigratedV5", "1");
                    localStorage.setItem("selectedColumns", JSON.stringify(fields));
                }
            }
            return fields;
        } catch (e) {
            return defaults();
        }
    }
    return defaults();
};

export const job_states = {
    in_preparation : "in preparation",
    todo: "todo",
    in_progress: "in progress",
    done: "done",
    failed: "failed"
};

export const proxy_types = (t) => {
    // Must stay a subset of PROVIDERS in pricescraper's proxy_provider_strategy: a value
    // that is not there resolves to no provider at all, silently unproxying the site.
    return  ['None', 'SmartProxyManager', 'Webshare', 'InHouseUnblocker', 'ZyteAPI', 'ScraperAPI', 'KamateraProxy']
        // , 'Operia', 'Oxylabs', 'OxylabsRealtime', 'OxylabsNGRP', 'IDataClickTeam', 'Zenrows', 'ZyteData', 'BrightData', 'NetNutRes', 'ZyteFetch', 'ProxyCrawl', 'SmartProxy'
};

export const proxy_countries = (t) => {
    return  ['ALL', 'APAC', 'AT', 'AU', 'BR', 'BE', 'CA', 'DE', 'EU', 'ES', 'FR', 'IE', 'IN', 'IT', 'MY', 'MX',
        'NL', 'PL', 'RU', 'SE', 'SG', 'TR', 'UK', 'US', 'UAE']
};

export const scraping_request_types = (t) => {
    return  ['limited_concurrent_requests']
};

export const yes_no = (t) => {

    return  [
        {label: t('yes_no.all'), value: null},
        {label: t('yes_no.yes'), value: true},
        {label: t('yes_no.no'), value: false}];
};

export const product_codes = (t) => {

    return  [
        {label: t('product.asin'), value: "asin"},
        {label: t('product.ean'), value: "ean"},
        {label: t('product.upc'), value: "upc"},
        {label: t('product.barcode'), value: "barcode"},
        {label: t('product.ref_id'), value: "ref_id"},
        {label: t('product.sku'), value: "sku"},
        {label: t('product.mpn'), value: "mpn"}];
};


export const getViolationCurrencyList = (selectedProduct) => {

    let currencies = [];

    if (selectedProduct.currency && selectedProduct.currency !== "")
        currencies.push({label: selectedProduct.currency, value : selectedProduct.currency});
    if (currencies.findIndex(c=> c.value === user().currency) === -1)
        currencies.push({label: user().currency, value: user().currency});

    if (currencies.findIndex(c=> c.value === 'USD') === -1)
        currencies.push({label: 'USD', value: 'USD'});
    if (currencies.findIndex(c=>  c.value === 'EUR') === -1)
        currencies.push({label: 'EUR', value: 'EUR'});

    if (selectedProduct.violation_strategy && currencies.findIndex(c=>  selectedProduct.violation_strategy && c.value === selectedProduct.violation_strategy.currency) === -1)
        currencies.push({label: selectedProduct.violation_strategy.currency, value: selectedProduct.violation_strategy.currency});

    return currencies;
    //{label:'CAD', value:'CAD'},
    // {label:'HKD', value:'HKD'},
    // {label:'ISK', value:'ISK'},
    // {label:'PHP', value:'PHP'},
    // {label:'DKK', value:'DKK'},
    // {label:'HUF', value:'HUF'},
    // {label:'CZK', value:'CZK'},
    // {label:'GBP', value:'GBP'},
    // {label:'RON', value:'RON'},
    // {label:'SEK', value:'SEK'},
    // {label:'IDR', value:'IDR'},
    // {label:'INR', value:'INR'},
    // {label:'BRL', value:'BRL'},
    // {label:'RUB', value:'RUB'},
    // {label:'HRK', value:'HRK'},
    // {label:'JPY', value:'JPY'},
    // {label:'THB', value:'THB'},
    // {label:'CHF', value:'CHF'},
    // {label:'MYR', value:'MYR'},
    // {label:'BGN', value:'BGN'},
    // {label:'CNY', value:'CNY'},
    // {label:'NOK', value:'NOK'},
    // {label:'NZD', value:'NZD'},
    // {label:'ZAR', value:'ZAR'},
    // {label:'MXN', value:'MXN'},
    // {label:'SGD', value:'SGD'},
    // {label:'AUD', value:'AUD'},
    // {label:'ILS', value:'ILS'},
    // {label:'KRW', value:'KRW'},
    // {label:'PLN', value:'PLN'}
};

export const user_types = (t) => {

    return  [
        {label: t('user.market_research'), value: null},
        {label: t('user.manufacturer'), value: true},
        {label: t('user.seller'), value: false}];
};

export const usage_types_values = [
    {value:"seller", label:'Seller'},
    {value:"manufacturer", label:'Manufacturer'},
    {value:"market_search", label:'Market Search'},
    {value:'drop_shipping', label:'Drop Shipping'}
    ];

export const scrapy_cloud_projects = [
    {value:"scraper", label:'Scraper'},
    {value:"selenium", label:'Selenium'},
    {value:"puppeteer", label:'Puppeteer'},
];

export const usage_types = (t) => {
    return [
        {value:"seller", label:t('campaign.usage_type_competitive')},
        {value:"manufacturer", label:t('campaign.usage_type_map_monitoring')},
        {value:"market_search", label:t('campaign.usage_type_market_research')},
        {value:'drop_shipping', label:t('campaign.usage_type_drop_shipping')}
    ];
};

// Icon + accent color per campaign usage type. Single source for the type badge
// used in the campaign cards and the topbar campaign selector.
export const usage_type_meta = {
    seller:        {icon: 'pi pi-bolt',   color: '#2563eb', bg: '#dbeafe', severity: 'info'},
    manufacturer:  {icon: 'pi pi-shield', color: '#dc2626', bg: '#fee2e2', severity: 'danger'},
    market_search: {icon: 'pi pi-search', color: '#16a34a', bg: '#dcfce7', severity: 'success'},
    drop_shipping: {icon: 'pi pi-truck',  color: '#d97706', bg: '#fef3c7', severity: 'warning'},
};
export const usageTypeMeta = (ut) => usage_type_meta[ut] || usage_type_meta.seller;

export const tagList = [
    // Excluded from admin KPIs (kept in sync with UserUtil.TEST_ACCOUNT_TAG
    // in the pricerest backend).
    "Test",
    "Eski Müşteri",
    "Alabilir",
    "Durumu Değerlendirelim",
    "Durum Bilgisi Al",
    "Teklif Yapıldı",
    "Demo Yapıldı",
    "Dönüş Bekleniyor",
    "Demo Yapılacak",
    "Mail Atılacak",
    "Aranacak",
    "Hesap Kurulacak",
    "Spiderlar Yazılacak"];

export const protectionStrategies = (t) => {
    return [{label:t('reprice.suggest_min_value'), value: "min_value"},
             {label:t('reprice.suggest_optimum_value'), value:"optimum_value"},
             {label:t('reprice.do_nothing'), value:"do_nothing"}];};

export const search_fields = [
    {name: 'Brand', code: 'brand'},
    {name: 'Name', code: 'name'},
    {name: 'Searh Key', code: 'search_key'},
    {name: 'Barcode', code: 'barcode'},
    {name: 'SKU', code: 'sku'},
    {name: 'MPN', code: 'mpn'},
    {name: 'EAN', code: 'ean'},
    {name: 'UPC', code: 'upc'},
    {name: 'Other Codes', code: 'other_codes'},
    {name: 'Cross SKU List', code: 'cross_sku'}
];

